export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface ArticleFaq {
  q: string;
  a: string;
}

export interface PillarArticle {
  slug: string;
  image: string;
  title: Record<string, string>;
  summary: Record<string, string>;
  intro: string;
  sections: ArticleSection[];
  faq: ArticleFaq[];
}

export const pillarArticles: PillarArticle[] = [
  {
    slug: "basetao-spreadsheet-guide",
    image: "https://readdy.ai/api/search-image?query=An%20open%20spreadsheet%20notebook%20with%20colorful%20category%20tabs%20and%20small%20product%20cards%20arranged%20on%20a%20clean%20white%20desk%20beside%20a%20warm%20cup%20of%20coffee%2C%20soft%20natural%20light%2C%20editorial%20productivity%20photography%2C%20calm%20organized%20workspace&width=1200&height=800&seq=art-guide-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Spreadsheet Guide: Organise Product Research",
      fr: "Guide Basetao Spreadsheet : Organiser la Recherche Produit",
      de: "Basetao Spreadsheet Leitfaden: Produktrecherche organisieren",
      es: "Guía de Basetao Spreadsheet: Organiza la Investigación",
      pt: "Guia Basetao Spreadsheet: Organize a Pesquisa",
    },
    summary: {
      en: "A practical walkthrough of how a structured spreadsheet helps you organise products, sellers and notes in one place.",
      fr: "Un guide pratique sur la façon dont une feuille structurée vous aide à organiser produits, vendeurs et notes en un seul endroit.",
      de: "Ein praktischer Überblick, wie eine strukturierte Tabelle hilft, Produkte, Verkäufer und Notizen an einem Ort zu organisieren.",
      es: "Una guía práctica de cómo una hoja estructurada te ayuda a organizar productos, vendedores y notas en un solo lugar.",
      pt: "Um guia prático de como uma planilha estruturada ajuda a organizar produtos, vendedores e notas em um só lugar.",
    },
    intro: "A structured spreadsheet turns scattered product research into a system you can actually reuse. Instead of losing links across dozens of tabs, a single sheet lets you group items, compare sellers and keep notes where you can find them later.",
    sections: [
      {
        heading: "Why Use a Structured Spreadsheet",
        paragraphs: [
          "The biggest advantage of a spreadsheet is consistency. Every product lives in the same row format, so you can sort by category, price or seller without re-reading your old notes.",
        ],
      },
      {
        heading: "What to Include in Your Sheet",
        paragraphs: [
          "Useful columns usually include the product link, category, seller name, estimated price, size or colour, and a short QC note. A dedicated tab for each category keeps long lists readable.",
        ],
      },
      {
        heading: "Tips for Keeping It Accurate",
        paragraphs: [
          "Prices and stock change often, so treat every figure as a starting point. Re-check the official listing before you order and update your sheet whenever a detail changes.",
        ],
      },
    ],
    faq: [
      { q: "Do I need any special software?", a: "No. Any spreadsheet tool works, from a simple online sheet to a dedicated app. The value comes from keeping the columns consistent." },
      { q: "How many columns should I use?", a: "Start with link, category, seller, price and notes. Add more columns only when a field actually helps your workflow." },
      { q: "Can I share my sheet?", a: "Yes, sharing is a common workflow, but remove any personal details and remember that figures may be out of date." },
    ],
  },
  {
    slug: "basetao-review",
    image: "https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20resting%20over%20a%20printed%20product%20comparison%20sheet%20with%20small%20shopping%20bags%20softly%20blurred%20in%20the%20background%2C%20soft%20studio%20light%2C%20editorial%20review%20photography%2C%20warm%20neutral%20tones&width=1200&height=800&seq=art-review-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Review: Shipping, QC, Warehouse & What to Know",
      fr: "Avis Basetao : Expédition, QC, Entrepôt et ce qu'il faut savoir",
      de: "Basetao Bewertung: Versand, QC, Lager und Wissenswertes",
      es: "Reseña de Basetao: Envío, QC, Almacén y lo que debes saber",
      pt: "Avaliação Basetao: Envio, QC, Armazém e o que saber",
    },
    summary: {
      en: "An independent overview of how the service works, what people commonly note, and what to verify before you order.",
      fr: "Un aperçu indépendant du fonctionnement du service, des points souvent remarqués et de ce qu'il faut vérifier avant de commander.",
      de: "Ein unabhängiger Überblick über die Funktionsweise, häufig genannte Punkte und was vor der Bestellung zu prüfen ist.",
      es: "Un resumen independiente de cómo funciona el servicio, lo que se suele destacar y qué verificar antes de pedir.",
      pt: "Uma visão independente de como o serviço funciona, o que costuma ser destacado e o que verificar antes de pedir.",
    },
    intro: "Basetao is an agent-based shopping service, which means it helps you buy from sellers, store items and forward them internationally. Understanding each step — from ordering to quality checks — helps you set the right expectations.",
    sections: [
      {
        heading: "How the Service Works",
        paragraphs: [
          "You typically submit a product link, the service purchases it on your behalf, and the item is stored in a warehouse before you choose a shipping method and send it home.",
        ],
      },
      {
        heading: "What Users Commonly Note",
        paragraphs: [
          "People often focus on shipping cost, QC photo quality and how clearly the interface communicates stock or order status. These are useful things to check before committing to a large order.",
        ],
      },
      {
        heading: "Things to Verify Before Ordering",
        paragraphs: [
          "Confirm current pricing, shipping options and any restrictions for your country. Read the latest official information rather than relying on older reviews that may no longer reflect how the service works.",
        ],
      },
    ],
    faq: [
      { q: "Is Basetao an official brand name?", a: "This guide is independent and does not claim any official affiliation. Always verify current details directly with the service." },
      { q: "What should I check first?", a: "Start with shipping options and QC support, since those most directly affect your final cost and confidence." },
      { q: "Are reviews always up to date?", a: "No. Services change frequently, so treat reviews as context and confirm the latest details yourself." },
    ],
  },
  {
    slug: "basetao-shipping-cost",
    image: "https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20with%20a%20calculator%20and%20printed%20shipping%20labels%20arranged%20on%20a%20light%20wooden%20table%2C%20soft%20natural%20light%2C%20clean%20logistics%20photography%2C%20warm%20minimal%20aesthetic&width=1200&height=800&seq=art-shipping-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Shipping Cost: Complete Guide",
      fr: "Frais d'expédition Basetao : Guide Complet",
      de: "Basetao Versandkosten: Vollständiger Leitfaden",
      es: "Coste de Envío Basetao: Guía Completa",
      pt: "Custo de Envio Basetao: Guia Completo",
    },
    summary: {
      en: "What drives shipping price, how to estimate your cost, and simple ways to keep international delivery manageable.",
      fr: "Ce qui influence le prix d'expédition, comment estimer votre coût et des moyens simples de maîtriser la livraison internationale.",
      de: "Was den Versandpreis beeinflusst, wie du deine Kosten schätzt und wie du den internationalen Versand im Griff behältst.",
      es: "Qué influye en el precio de envío, cómo estimar tu coste y formas sencillas de controlar la entrega internacional.",
      pt: "O que influencia o preço de envio, como estimar seu custo e maneiras simples de controlar a entrega internacional.",
    },
    intro: "Shipping is often the part of an order that surprises people most. Because cost depends on weight, dimensions and destination, a little planning can help you avoid paying more than you expected.",
    sections: [
      {
        heading: "What Affects Shipping Price",
        paragraphs: [
          "Weight and package size are the main drivers, but the destination country, carrier and shipping speed all play a role. Some routes also include duties or handling fees.",
        ],
      },
      {
        heading: "How to Estimate Your Cost",
        paragraphs: [
          "Keep rough weights for the items you buy and compare a couple of shipping lines before committing. Small differences in packaging can change the final quote noticeably.",
        ],
      },
      {
        heading: "Ways to Keep Shipping Manageable",
        paragraphs: [
          "Consolidating several items into one parcel usually costs less per item. Removing bulky packaging can also help, as long as it does not compromise protection.",
        ],
      },
    ],
    faq: [
      { q: "Why does shipping vary so much?", a: "Weight, dimensions, destination and carrier all change the price, so two similar orders can cost very differently." },
      { q: "Is consolidation cheaper?", a: "Usually yes, because you pay one parcel's base fee instead of several." },
      { q: "Should I always pick the cheapest line?", a: "Not always. Balance cost against speed and tracking so you do not sacrifice reliability for a small saving." },
    ],
  },
  {
    slug: "basetao-warehouse",
    image: "https://readdy.ai/api/search-image?query=A%20bright%20modern%20warehouse%20interior%20with%20neatly%20stacked%20cardboard%20boxes%20and%20tall%20shelving%2C%20soft%20daylight%20streaming%20through%20large%20windows%2C%20clean%20industrial%20photography%2C%20airy%20organized%20atmosphere&width=1200&height=800&seq=art-warehouse-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Warehouse: Storage & Consolidation Explained",
      fr: "Entrepôt Basetao : Stockage et Regroupement Expliqués",
      de: "Basetao Lager: Lagerung und Konsolidierung erklärt",
      es: "Almacén Basetao: Almacenamiento y Consolidación",
      pt: "Armazém Basetao: Armazenamento e Consolidação",
    },
    summary: {
      en: "How warehouse storage and consolidation work, and how to use them to group items before a single shipment.",
      fr: "Comment fonctionnent le stockage en entrepôt et le regroupement, et comment les utiliser pour regrouper des articles avant un seul envoi.",
      de: "Wie Lagerung und Konsolidierung funktionieren und wie du sie nutzt, um Artikel vor einem einzigen Versand zu bündeln.",
      es: "Cómo funcionan el almacenamiento y la consolidación, y cómo usarlos para agrupar artículos antes de un solo envío.",
      pt: "Como funcionam o armazenamento e a consolidação, e como usá-los para agrupar itens antes de um único envio.",
    },
    intro: "A warehouse is the middle step between buying and shipping. Your items are stored there until you decide which ones to send together, which gives you more control over the final parcel.",
    sections: [
      {
        heading: "What a Warehouse Does",
        paragraphs: [
          "It receives your purchases, stores them safely and keeps them ready for consolidation. This lets you order from several sellers and ship everything as one parcel later.",
        ],
      },
      {
        heading: "Consolidation Explained",
        paragraphs: [
          "Consolidation combines multiple stored items into a single package. This usually reduces the total shipping cost and simplifies tracking.",
        ],
      },
      {
        heading: "Tips for Managing Your Haul",
        paragraphs: [
          "Check storage limits and any free storage period before you buy in bulk. Group items that you want to ship at the same time so nothing waits too long.",
        ],
      },
    ],
    faq: [
      { q: "How long can items stay in storage?", a: "Storage windows vary, so check the current policy before you buy more than you can ship quickly." },
      { q: "Does consolidation always save money?", a: "It often does, but very large or fragile items may need special handling that changes the cost." },
      { q: "Can I ship items separately?", a: "Yes, you can usually choose which items to ship and when, giving you flexibility over timing." },
    ],
  },
  {
    slug: "basetao-qc",
    image: "https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20inspecting%20a%20folded%20garment%20with%20a%20printed%20checklist%20and%20a%20digital%20camera%20on%20a%20clean%20table%2C%20soft%20studio%20lighting%2C%20quality%20control%20photography%2C%20fresh%20clean%20aesthetic&width=1200&height=800&seq=art-qc-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao QC: How Quality Checks Work",
      fr: "QC Basetao : Comment fonctionnent les contrôles qualité",
      de: "Basetao QC: So funktionieren Qualitätskontrollen",
      es: "QC de Basetao: Cómo funcionan los controles de calidad",
      pt: "QC Basetao: Como funcionam os controles de qualidade",
    },
    summary: {
      en: "What QC photos show, how to read a quality report, and how checks help you catch problems before shipping.",
      fr: "Ce que montrent les photos QC, comment lire un rapport qualité et comment les contrôles aident à repérer les problèmes avant l'envoi.",
      de: "Was QC-Fotos zeigen, wie du einen Qualitätsbericht liest und wie Kontrollen helfen, Probleme vor dem Versand zu erkennen.",
      es: "Qué muestran las fotos de QC, cómo leer un informe de calidad y cómo ayudan los controles a detectar problemas antes del envío.",
      pt: "O que as fotos de QC mostram, como ler um relatório de qualidade e como os controles ajudam a detectar problemas antes do envio.",
    },
    intro: "Quality control is your chance to see the item before it ships. A few clear photos can reveal defects, wrong colours or sizing issues that are much harder to fix after delivery.",
    sections: [
      {
        heading: "Why QC Matters",
        paragraphs: [
          "Because you cannot hold the product before buying, QC photos bridge that gap. They let you spot obvious issues while the item is still in the warehouse.",
        ],
      },
      {
        heading: "What QC Photos Show",
        paragraphs: [
          "Typical photos include the item from a few angles, close-ups of labels or logos, and sometimes measurements. The goal is to confirm the item matches the listing.",
        ],
      },
      {
        heading: "How to Read a QC Report",
        paragraphs: [
          "Compare the photos against the seller's listing and your size notes. If something looks off, ask for another photo or clarify before the item ships.",
        ],
      },
    ],
    faq: [
      { q: "Is QC always available?", a: "It depends on the service and item. Check whether QC is offered and whether it costs extra before you rely on it." },
      { q: "What if QC shows a problem?", a: "You can usually request a return or exchange while the item is still stored, which is easier than fixing it after delivery." },
      { q: "Do QC photos guarantee a perfect item?", a: "No. Photos reduce risk but cannot catch every detail, so review them carefully." },
    ],
  },
  {
    slug: "basetao-coupon",
    image: "https://readdy.ai/api/search-image?query=A%20paper%20discount%20coupon%20ticket%20and%20a%20smartphone%20showing%20a%20promo%20code%20on%20a%20soft%20pastel%20background%2C%20warm%20gentle%20light%2C%20clean%20promotional%20photography%2C%20cheerful%20minimal%20look&width=1200&height=800&seq=art-coupon-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Coupon & Discount Code: Updated Guide",
      fr: "Coupon et Code Promo Basetao : Guide à Jour",
      de: "Basetao Coupon & Rabattcode: Aktualisierter Leitfaden",
      es: "Cupón y Código de Descuento Basetao: Guía",
      pt: "Cupom e Código de Desconto Basetao: Guia Atualizado",
    },
    summary: {
      en: "How coupons and discount codes usually work, where to look for them, and what to watch out for before you use one.",
      fr: "Comment fonctionnent généralement les coupons et codes promo, où les chercher et à quoi faire attention avant de les utiliser.",
      de: "Wie Coupons und Rabattcodes funktionieren, wo du sie findest und worauf du vor der Nutzung achten solltest.",
      es: "Cómo funcionan los cupones y códigos de descuento, dónde buscarlos y qué vigilar antes de usarlos.",
      pt: "Como funcionam os cupons e códigos de desconto, onde procurá-los e o que observar antes de usar.",
    },
    intro: "Discount codes are a nice bonus, but they change frequently and rarely last long. Knowing where to look and how to confirm a code is valid helps you use them without frustration.",
    sections: [
      {
        heading: "How Coupons Usually Work",
        paragraphs: [
          "A code is applied at checkout to reduce shipping or service fees. Codes often have an expiry date, a minimum spend, or a limit on how many times they can be used.",
        ],
      },
      {
        heading: "Where to Look",
        paragraphs: [
          "Check the official site and any official announcements first. Codes shared on forums or social media may be outdated or no longer valid.",
        ],
      },
      {
        heading: "What to Watch Out For",
        paragraphs: [
          "Avoid clicking unfamiliar links promising guaranteed discounts. Verify a code directly at checkout, and never share personal details to obtain one.",
        ],
      },
    ],
    faq: [
      { q: "Do codes last long?", a: "Usually not. Treat them as time-limited and confirm they still work before you count on the saving." },
      { q: "Where should I look first?", a: "The official site and its announcements, since third-party codes are often expired or inaccurate." },
      { q: "Are all shared codes safe?", a: "No. Be cautious with links from unknown sources and verify codes only through the official checkout." },
    ],
  },
  {
    slug: "is-basetao-legit",
    image: "https://readdy.ai/api/search-image?query=A%20soft%20glowing%20protective%20shield%20with%20a%20checklist%20and%20a%20padlock%20on%20a%20warm%20neutral%20background%2C%20trust%20and%20security%20concept%2C%20clean%20minimal%20illustrative%20photography&width=1200&height=800&seq=art-legit-01&orientation=landscape&nocache=true",
    title: {
      en: "Is Basetao Legit? Safety & Buyer Guide",
      fr: "Basetao est-il légitime ? Guide de Sécurité et d'Achat",
      de: "Ist Basetao seriös? Sicherheits- und Käuferleitfaden",
      es: "¿Es Basetao Legítimo? Guía de Seguridad",
      pt: "O Basetao é Legítimo? Guia de Segurança",
    },
    summary: {
      en: "How to evaluate the service yourself, practical ways to stay safe, and red flags that are worth avoiding.",
      fr: "Comment évaluer le service vous-même, des moyens pratiques de rester en sécurité et les signaux d'alarme à éviter.",
      de: "Wie du den Dienst selbst bewertest, wie du sicher bleibst und welche Warnzeichen du vermeiden solltest.",
      es: "Cómo evaluar el servicio tú mismo, formas de mantenerte seguro y señales de alerta que conviene evitar.",
      pt: "Como avaliar o serviço você mesmo, maneiras de se manter seguro e sinais de alerta a evitar.",
    },
    intro: "\"Is it legit?\" is the right first question for any agent-based service. Rather than trusting a single opinion, the safest approach is to check a few concrete signals before you spend money.",
    sections: [
      {
        heading: "Understanding the Service",
        paragraphs: [
          "Basetao acts as an intermediary that buys and forwards goods. This model is common, but the quality of support, QC and shipping can vary, so confirm how each part works for your situation.",
        ],
      },
      {
        heading: "How to Stay Safe",
        paragraphs: [
          "Start with a small test order, keep records of your orders and payments, and only use official channels to contact support. Verify the official domain before logging in or paying.",
        ],
      },
      {
        heading: "Red Flags to Avoid",
        paragraphs: [
          "Be wary of unsolicited messages, links to lookalike websites, and anyone asking for payment outside the official checkout. These are signs of phishing rather than the real service.",
        ],
      },
    ],
    faq: [
      { q: "Can I verify the service myself?", a: "Yes. Check the official domain, support channels and recent user feedback, and start with a small order." },
      { q: "What is the biggest risk?", a: "Phishing via lookalike sites or fake links, so always confirm the URL before entering details." },
      { q: "Should I start small?", a: "Yes, a small test order is the lowest-risk way to judge the service before a larger purchase." },
    ],
  },
  {
    slug: "basetao-vs-superbuy",
    image: "https://readdy.ai/api/search-image?query=Two%20opposing%20product%20boxes%20standing%20side%20by%20side%20on%20a%20light%20surface%20with%20a%20versus%20sign%20between%20them%2C%20soft%20studio%20light%2C%20clean%20comparison%20concept%20photography%2C%20minimal%20modern%20aesthetic&width=1200&height=800&seq=art-vs-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao vs Superbuy: An Honest Comparison",
      fr: "Basetao vs Superbuy : Une Comparaison Honnête",
      de: "Basetao vs Superbuy: Ein ehrlicher Vergleich",
      es: "Basetao vs Superbuy: Una Comparación Honesta",
      pt: "Basetao vs Superbuy: Uma Comparação Honesta",
    },
    summary: {
      en: "A neutral look at how two agent services compare on shipping, QC, fees and the overall experience.",
      fr: "Un regard neutre sur la comparaison de deux services d'agent concernant l'expédition, le QC, les frais et l'expérience globale.",
      de: "Ein neutraler Blick darauf, wie sich zwei Agent-Dienste bei Versand, QC, Gebühren und dem Gesamterlebnis unterscheiden.",
      es: "Una mirada neutral a cómo se comparan dos servicios de agente en envío, QC, tarifas y experiencia general.",
      pt: "Um olhar neutro sobre como dois serviços de agente se comparam em envio, QC, taxas e experiência geral.",
    },
    intro: "Both Basetao and Superbuy are agent-based services with a similar core: buy, store and forward. The differences usually show up in fees, QC options and how each interface presents shipping choices.",
    sections: [
      {
        heading: "Service Overview",
        paragraphs: [
          "Both services help you purchase from sellers and forward internationally. The practical differences are less about what they do and more about how transparently they price each step.",
        ],
      },
      {
        heading: "Key Differences",
        paragraphs: [
          "Compare shipping line options, QC photo quality, storage limits and fee structure side by side. A small difference in per-kilo pricing can add up on a large haul.",
        ],
      },
      {
        heading: "How to Choose",
        paragraphs: [
          "Run a small test order on each if you are undecided. Judge the actual quote, the QC photos and the support speed rather than a single review or ranking.",
        ],
      },
    ],
    faq: [
      { q: "Which service is better?", a: "There is no universal answer. The right choice depends on your destination, item type and which features you value." },
      { q: "Should I compare prices?", a: "Yes, but compare total landed cost, including fees and shipping, not just the item price." },
      { q: "Can I use both?", a: "Yes, many people use more than one service to get the best fit for different orders." },
    ],
  },
  {
    slug: "basetao-shoes-spreadsheet",
    image: "https://readdy.ai/api/search-image?query=A%20collection%20of%20stylish%20sneakers%20arranged%20neatly%20on%20top%20of%20a%20spreadsheet%20grid%20print%20background%2C%20soft%20natural%20light%2C%20fashion%20product%20photography%2C%20fresh%20editorial%20look&width=1200&height=800&seq=art-shoes-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Shoes Spreadsheet: Category Guide",
      fr: "Feuille Chaussures Basetao : Guide de Catégorie",
      de: "Basetao Schuhe-Tabelle: Kategorie-Leitfaden",
      es: "Hoja de Zapatos Basetao: Guía de Categoría",
      pt: "Planilha de Sapatos Basetao: Guia de Categoria",
    },
    summary: {
      en: "Why shoes are a popular category, what to record for each pair, and how to avoid common sizing mistakes.",
      fr: "Pourquoi les chaussures sont une catégorie populaire, quoi noter pour chaque paire et comment éviter les erreurs de taille.",
      de: "Warum Schuhe eine beliebte Kategorie sind, was du für jedes Paar notierst und wie du Größenschwierigkeiten vermeidest.",
      es: "Por qué los zapatos son una categoría popular, qué anotar para cada par y cómo evitar errores comunes de talla.",
      pt: "Por que os sapatos são uma categoria popular, o que registrar para cada par e como evitar erros comuns de tamanho.",
    },
    intro: "Shoes are one of the most researched categories because sizing varies so much between brands and sellers. A dedicated spreadsheet helps you track measurements instead of guessing every time.",
    sections: [
      {
        heading: "Why Shoes Are Popular",
        paragraphs: [
          "Footwear offers a huge variety of styles and price points, which makes comparison useful. The challenge is that a single size label rarely tells you how a pair will actually fit.",
        ],
      },
      {
        heading: "What to Record",
        paragraphs: [
          "For each pair, note the listed size, the insole length in centimetres if available, the seller's sizing advice and any QC photos of the actual measurements.",
        ],
      },
      {
        heading: "Avoiding Common Sizing Mistakes",
        paragraphs: [
          "Do not rely on your usual size alone. Compare insole measurements against a pair you already own, and check seller feedback about whether the shoe runs large or small.",
        ],
      },
    ],
    faq: [
      { q: "Why does sizing vary so much?", a: "Different factories and regions use different lasts, so the same label can fit very differently." },
      { q: "What measurement matters most?", a: "Insole length is usually the most useful, so compare it against a shoe that already fits well." },
      { q: "Should I size up?", a: "Not automatically. Use measurements and seller feedback to decide case by case." },
    ],
  },
  {
    slug: "basetao-beginner-guide",
    image: "https://readdy.ai/api/search-image?query=A%20persons%20hand%20pointing%20at%20the%20first%20page%20of%20an%20open%20beginner%20guide%20notebook%20with%20colorful%20sticky%20notes%2C%20warm%20natural%20light%2C%20welcoming%20educational%20photography&width=1200&height=800&seq=art-beginner-01&orientation=landscape&nocache=true",
    title: {
      en: "Basetao Beginner Guide: Start Here",
      fr: "Guide Débutant Basetao : Commencez Ici",
      de: "Basetao Einsteiger-Leitfaden: Hier starten",
      es: "Guía para Principiantes Basetao: Empieza Aquí",
      pt: "Guia para Iniciantes Basetao: Comece Aqui",
    },
    summary: {
      en: "A simple first-steps walkthrough, the terms worth knowing, and how to build your first product list without confusion.",
      fr: "Un parcours simple des premières étapes, les termes à connaître et comment construire votre première liste sans confusion.",
      de: "Eine einfache Schritt-für-Schritt-Einführung, wichtige Begriffe und wie du deine erste Liste ohne Verwirrung aufbaust.",
      es: "Un recorrido simple por los primeros pasos, los términos que conviene conocer y cómo crear tu primera lista sin confusiones.",
      pt: "Um passo a passo simples dos primeiros passos, os termos que vale conhecer e como montar sua primeira lista sem confusão.",
    },
    intro: "Starting with an agent service feels complicated until you understand the basic flow: find a product, place the order, wait for storage, then ship. Once you see it as four steps, everything else makes sense.",
    sections: [
      {
        heading: "Your First Steps",
        paragraphs: [
          "Start by choosing one small item from a category you know well. Submit the link, confirm the order details, and watch how the status moves through purchase, storage and shipping.",
        ],
      },
      {
        heading: "Common Terms to Know",
        paragraphs: [
          "QC means quality control, consolidation means combining items into one parcel, and a haul is simply the group of items you plan to ship together.",
        ],
      },
      {
        heading: "Building Your First List",
        paragraphs: [
          "Keep a simple spreadsheet with the product link, price and any notes. As you add more items, sort them by category so you can compare and consolidate easily.",
        ],
      },
    ],
    faq: [
      { q: "What should my first order be?", a: "A single, inexpensive item in a familiar category, so you can learn the flow with minimal risk." },
      { q: "Do I need a spreadsheet as a beginner?", a: "It is optional at first, but a simple list quickly becomes useful as your orders grow." },
      { q: "How long does the process take?", a: "It varies by seller, warehouse and shipping line, so set flexible expectations and track each step." },
    ],
  },
];